import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"

import { cn } from "../lib/utils"

// `ring-offset-background` / `ring-ring` reference --background and --ring
// which this app doesn't define globally (same root cause as the dialog
// component's bg fallback). With those undefined the focus-visible ring
// renders as a thin sliver on the right edge of buttons. Pin both to
// explicit Tailwind colors so the ring (when it does appear on keyboard
// focus) sits cleanly on a white offset.
const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 ring-offset-white disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-blue-200 text-black-800 hover:bg-blue-300 border border-blue-200 shadow-lg rounded-full",
        black: "bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 border border-destructive rounded-full",
        outline: "border border-input hover:bg-accent hover:text-accent-foreground rounded-full",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-secondary rounded-full",
        ghost: "hover:bg-accent hover:text-accent-foreground rounded-full",
        link: "underline-offset-4 hover:underline text-primary",
        unstyled: "",
      },
      size: {
        default: "h-10 py-2 px-4",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className)}
      ref={ref}
      {...props}
    />
  )
})

Button.displayName = "Button"

export { Button, buttonVariants }